import * as React from 'react'
import * as cn from 'classnames'
import * as styles from './Hover.module.scss'
import { WatchlistAction, DVString, ItemMaturityRating, TrailerFragment } from 'DVWebNodeUtils'
import WatchlistStatus from './WatchlistStatus'
import { InfoboxProps, Infobox } from './Infobox'
import { Icon, IconType, Badge, badges, Hover as BasicHover } from 'DVWebNodeUI'
import { CommonPlaybackAction } from './Headline/PlaybackBlock'
import { ReviewsRating } from './ReviewsRating'
import { Tooltip } from './Tooltip'
import { Headline } from './Headline'
import { CustomerReviews } from 'util/types/wps/hover'

interface HoverProps {
    title?: string
    titleID?: string
    currentBookmarkLabel?: string
    synopsis?: string
    tvShowSeasonLabel?: string
    hasSubtitles?: boolean
    regulatoryRating?: string
    watchlistAction?: WatchlistAction
    className?: string
    children: React.ReactChild | React.ReactChild[]
    maturityRating?: ItemMaturityRating
    imdbRating?: string
    releaseYear?: string | number
    runtime?: string
    liveBadgeLabel?: string
    playbackAction?: CommonPlaybackAction
    trailer?: TrailerFragment
    placeholderClassName?: string
    offers?: DVString[]
    infoboxes?: InfoboxProps[]
    customerReviews?: CustomerReviews
    onMouseOver?: (event: React.MouseEvent<HTMLDivElement>) => void
    onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void
    onWatchlistToggle?: (newAction: WatchlistAction) => void
}


export interface RegulatoryRatingBadgeProps {
    displayText: string;
    description: string;
    countryCode?: string;
    simplifiedId?: string;
}


const TrailerWrapper = ({ trailer, children }: { trailer: TrailerFragment, children: any }) => (
    <Tooltip text={trailer.text}>
        <a
            className={'js-deeplinkable'}
            href={trailer.playbackURL}
            data-page-title-id={trailer.playbackID}
            data-video-type={trailer.videoMaterialType}
            data-use-full-view="true"
            data-is-trailer="true"
            data-title-id={trailer.playbackID}
            data-ux-click="true">
            {children}
        </a>
    </Tooltip>
)

const IconWrapper = ({ children }: { children: any }) => (
    <div className={cn(styles.iconWrapper, styles.alignBottom, styles.spaceRight)}>
        {children}
    </div>
)


export class Hover extends React.Component<HoverProps> {

    constructor(props: HoverProps) {
        super(props)

    }

    render() {
        const watchlistAction = this.props.watchlistAction && { ...this.props.watchlistAction, text: this.props.watchlistAction.text as DVString }

        const preFirstLine = this.props.liveBadgeLabel &&
            <div className={styles.preFirstLine}>
                <Badge className={styles.tailContent}
                    use={badges.LIVE}>
                    {this.props.liveBadgeLabel}
                </Badge>
            </div>

        const firstline = <div className={styles.firstLine}>
            {preFirstLine}
            <Headline playbackAction={this.props.playbackAction}
                offers={this.props.offers}
                title={this.props.title} />
            <div className={styles.additionalIcons}>
                {this.props.trailer &&
                    <IconWrapper>
                        <TrailerWrapper
                            trailer={this.props.trailer} >
                            <Icon use={IconType.TRAILER} className={cn(styles.tailContent, styles.alignBottom)} />
                        </TrailerWrapper>
                    </IconWrapper>}
                {watchlistAction &&
                    <IconWrapper>
                        <WatchlistStatus
                            onToggle={this.props.onWatchlistToggle}
                            watchlistAction={watchlistAction}
                        />
                    </IconWrapper>}
            </div>
        </div>


        const boxes = this.props.infoboxes &&
            this.props.infoboxes.map(infobox => <Infobox {...infobox} />)

        const synopsis = <p className={styles.synopsis}>{this.props.synopsis}</p>

        const extraMetadata = <div className={styles.extraMetadata}>
            {this.props.customerReviews &&
                <ReviewsRating rating={this.props.customerReviews.value}
                    className={styles.tailContent} />}
            {this.props.imdbRating &&
                <span className={cn(styles.tailContent, styles.tailText)}>
                    IMDb {this.props.imdbRating}
                </span>}
            {this.props.runtime &&
                <span className={cn(styles.tailContent, styles.tailText)}>
                    {this.props.runtime}
                </span>}
            {this.props.releaseYear &&
                <span className={cn(styles.tailContent, styles.tailText)}>
                    {this.props.releaseYear}
                </span>}
            {this.props.hasSubtitles &&
                <Icon use={IconType.SUBTITLES} className={styles.tailContent} />}
            {this.props.maturityRating &&
                <Badge use={badges.REGULATORY_RATING} className={styles.tailContent}>{this.props.maturityRating.rating}</Badge>}
        </div>

        const blocks = [firstline, boxes, synopsis, extraMetadata].filter(line => Boolean(line))

        return (
            <BasicHover
                contentBlocks={blocks}
                className={this.props.className}
                placeholderClassName={this.props.placeholderClassName}
                onMouseLeave={this.props.onMouseLeave}
                onMouseOver={this.props.onMouseOver}>
                {this.props.children}
            </BasicHover>
        )
    }
}